/**
 * 場後結算 · 表單（P1-10；`ui-spec` 頁 12）
 *
 * 為何抽離（硬規則 B：頁面檔 ≤200 行）：對數摘要 ＋ 逐項輸入 ＋ 兩個掣，擺入頁面檔會爆。
 *
 * 紅線（前端只係提示；**後端 ＋ 模組才係權威**）
 *   · ⚠️ 對數（`G1`）同摘要（`reconcileSummary()`）**一律**用
 *     `@coeldery/event-reconciliation` —— 前端**唔准**自己再寫一份（R1：唯一實作）
 *   · ⚠️ 帳目（`SALE`／`TRANSFER`／`ADJUST`）**只由後端**產生（確認入帳時）；
 *     前端唔碰帳 —— 呢度只提交數量同去向
 *   · ⚠️ 確認之後唔准改（`B1` append-only）：要更正 ＝ 另開一筆調整
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, card, errText, grid2, hint as hintStyle } from '@coeldery/ui-kit';
import {
  reconcileErrors,
  reconcileSummary,
  type ReconcileLine,
  type ReconcileLineContext,
} from '@coeldery/event-reconciliation';
import ReconcileRow from './ReconcileRow';
import {
  emptyRow,
  reconcileContexts,
  rowFromDb,
  rowToLine,
  toInputLines,
  type RowState,
} from '../lib/reconcile';
import { ApiError, api, type ReconcileDetail, type ReconcileResult } from '../lib/api';

export interface ReconcileFormProps {
  data: ReconcileDetail;
  /** 存檔成功（頁面負責重新載入；`confirmed` ＝ 今次係確認入帳） */
  onSaved: (result: ReconcileResult) => Promise<void>;
}

export default function ReconcileForm({ data, onSaved }: ReconcileFormProps) {
  const { t, i18n } = useTranslation();
  const order = data.order;
  const savedByLine = new Map(data.lines.map((l) => [l.outboundLineId, l]));

  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(data.base.map((b) => [b.lineId, rowFromDb(savedByLine.get(b.lineId))])),
  );
  const [reportedAt, setReportedAt] = useState<string | null>(
    data.reconciliation?.reportedAt ?? new Date().toISOString().slice(0, 10),
  );
  const [venue, setVenue] = useState<string | null>(data.reconciliation?.venue ?? null);
  const [note, setNote] = useState<string | null>(data.reconciliation?.note ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const row = (lineId: string): RowState => rows[lineId] ?? emptyRow();
  const patch = (lineId: string, p: Partial<RowState>) =>
    setRows((r) => ({ ...r, [lineId]: { ...row(lineId), ...p } }));

  /** 對數基準（模組要嘅 `ReconcileLineContext`；`outCartons` ＝ **實際執到**） */
  const ctxs: ReconcileLineContext[] = reconcileContexts(data.base, order.orderNo);
  const models: ReconcileLine[] = data.base.map((b) => rowToLine(b, row(b.lineId)));
  /** ⭐ 驗證同摘要都用模組（唯一實作）—— 前端唔自己計 */
  const issues = reconcileErrors(models, ctxs);
  const summary = reconcileSummary(models, ctxs);

  const submit = async (confirm: boolean) => {
    setError(null);
    if (confirm && !window.confirm(t('recon.confirmAsk', { orderNo: order.orderNo }))) return;
    // 前端先擋（同後端／模組一致）—— 草稿都唔准存假帳（`G1`）
    if (issues.length) {
      setError(`${t('recon.needAllRows')}：${issues[0].message}`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.saveReconciliation(order.id, {
        reportedAt,
        venue,
        note,
        confirm,
        lines: toInputLines(models),
      });
      await onSaved(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit(false);
  };

  return (
    <form onSubmit={onSubmit} style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <p style={hintStyle}>{t('recon.balanceHint')}</p>
      <div style={grid2}>
        <FormTextField
          id="f-rc-venue"
          labelKey="recon.venue"
          value={venue}
          onChange={setVenue}
        />
        <FormTextField
          id="f-rc-date"
          labelKey="recon.reportedAt"
          type="date"
          value={reportedAt}
          onChange={setReportedAt}
        />
      </div>
      <div style={{ marginTop: '8px' }}>
        <FormTextField id="f-rc-note" labelKey="recon.note" value={note} onChange={setNote} />
      </div>

      <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
        {data.base.map((b, i) => (
          <ReconcileRow
            key={b.lineId}
            base={b}
            ctx={ctxs[i]}
            row={row(b.lineId)}
            onChange={(p) => patch(b.lineId, p)}
            readOnly={false}
            dispositions={data.dispositions}
            roadshowTargets={data.roadshowTargets}
            storeTargets={data.storeTargets}
            lang={i18n.language}
          />
        ))}
      </ul>

      <h3 style={{ margin: '12px 0 6px', fontSize: '16px', fontWeight: 800 }}>
        {t('recon.summary')}
      </h3>
      <p style={hintStyle}>
        {t('recon.outCartons')}：{summary.outCartons} {t('recon.cartons')} · {t('recon.sold')}：
        {summary.soldUnits} {t('recon.units')} · {t('recon.leftover')}：{summary.leftoverUnits}{' '}
        {t('recon.units')} · {t('recon.damaged')}：{summary.damagedUnits} {t('recon.units')}
      </p>
      <p style={hintStyle}>
        {t('recon.summaryLines', { ok: summary.balancedLines, total: summary.itemCount })}
        {summary.missingDisposition > 0
          ? ` · ${t('recon.missingDisposition', { n: summary.missingDisposition })}`
          : ''}
      </p>
      <p style={summary.balanced ? hintStyle : errText}>
        {summary.balanced
          ? t('recon.balancedOk')
          : t('recon.unbalancedLines', { n: summary.unbalancedLines })}
      </p>
      {data.reconciliation?.status === 'draft' && <p style={hintStyle}>{t('recon.draftHint')}</p>}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
        <button type="submit" style={btnGhost} disabled={busy}>
          {busy ? t('common.saving') : t('recon.saveDraft')}
        </button>
        <button
          type="button"
          style={btnPrimary}
          disabled={busy || !summary.balanced}
          onClick={() => void submit(true)}
        >
          {busy ? t('common.saving') : t('recon.confirm')}
        </button>
      </div>
      <p style={hintStyle}>{t('recon.confirmHint')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

